import { useState, useEffect, useMemo, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Assessment, Category } from '@/types/assessment';
import { useAssessmentProgress } from '@/hooks/useAssessmentProgress';
import { useAssessmentResponses } from '@/hooks/useAssessmentResponses';
import { calculateProgressPercentage, isQuestionFullyAnswered } from '@/utils/assessmentHelpers';
import { validateAllRequiredQuestionsAnswered } from '@/hooks/assessment/responseUtils';
import AssessmentLayout from '@/components/assessment-portal/AssessmentLayout';
import AssessmentProgress from '@/components/assessment-portal/AssessmentProgress';
import AssessmentContent from '@/components/assessment-portal/AssessmentContent';
import PremiumAssessmentHeader from '@/components/premium-dashboard/PremiumAssessmentHeader';
import RenameAssessmentDialog from '@/components/premium-dashboard/RenameAssessmentDialog';
import { supabase } from '@/integrations/supabase/client';
import { buildPrefilledResponsesFromAnswerDetails } from './premiumAssessmentPrefill';
import { deleteAttempt, updateAttemptName, loadAttempt, isAttemptNameTaken } from '@/services/premiumProgressService';
import { clearProgressFromLocalStorage } from '@/hooks/assessment/progressUtils';

interface PremiumAssessmentProps {
  assessment: Assessment;
  onBack: () => void;
  /** Required for premium flows. Identifies the specific in-progress attempt row. */
  attemptId: string;
  /** Whether this is a brand new attempt (skip pre-fill from old data, prompt for name). */
  isFreshAttempt?: boolean;
  /** Initial custom name to seed the rename dialog / header. */
  initialCustomName?: string | null;
}

/**
 * Premium assessment flow: includes ALL questions (free + premium, non-disabled).
 * Each attempt is identified by attemptId and persisted independently.
 */
const PremiumAssessment = ({
  assessment,
  onBack,
  attemptId,
  isFreshAttempt = false,
  initialCustomName = null,
}: PremiumAssessmentProps) => {
  const [userEmail, setUserEmail] = useState('');
  const [freeResponsesLoaded, setFreeResponsesLoaded] = useState(false);
  const [customName, setCustomName] = useState<string | null>(initialCustomName);
  const [renameOpen, setRenameOpen] = useState(false);
  // Show the naming dialog right after "Let's get started" on a fresh attempt.
  const [pendingNamePrompt, setPendingNamePrompt] = useState(isFreshAttempt);
  const { toast } = useToast();

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user?.email) setUserEmail(user.email);
    });
  }, []);

  // Hydrate latest custom_name from DB in case it changed elsewhere.
  useEffect(() => {
    let cancelled = false;
    loadAttempt(attemptId).then((attempt) => {
      if (cancelled || !attempt) return;
      if (attempt.custom_name && attempt.custom_name !== customName) {
        setCustomName(attempt.custom_name);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptId]);

  // Get all categories with all non-disabled questions (free first, then premium)
  const sortedCategories = useMemo(() => getAllCategoriesWithQuestions(assessment), [assessment]);

  const {
    currentStep,
    currentCategoryIndex,
    currentQuestionIndex,
    currentCategory,
    currentQuestion,
    personalInfo,
    setPersonalInfo,
    handleStartAssessment,
    handleRestartAssessment,
    moveToNextQuestion,
    moveToPreviousQuestion,
    moveBackToQuestions,
    navigateToQuestion,
    completeAssessment,
  } = useAssessmentProgress({ assessment, categories: sortedCategories, attemptId });

  // When resuming an ongoing attempt, skip the "Let's get started" welcome page
  // and jump straight into the questions.
  const skippedWelcomeRef = useRef(false);
  useEffect(() => {
    if (!isFreshAttempt && currentStep === 'welcome' && !skippedWelcomeRef.current) {
      skippedWelcomeRef.current = true;
      handleStartAssessment();
    }
  }, [isFreshAttempt, currentStep, handleStartAssessment]);

  const {
    responses,
    calculatedScores,
    handleResponseChange,
    canMoveForward,
    submitAssessment,
    isSubmitting,
    clearAllResponses,
    batchSetResponses,
  } = useAssessmentResponses({ assessment, categories: sortedCategories, attemptId });

  // Pre-fill with latest free assessment responses (only on fresh attempts)
  useEffect(() => {
    if (!userEmail || freeResponsesLoaded || !isFreshAttempt) return;

    const fetchFreeResponses = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const response = await supabase.functions.invoke('get-my-responses', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });

        if (response.data && Array.isArray(response.data)) {
          const premiumQuestionIds = new Set<string>();
          assessment.categories.forEach((c: any) => {
            c.questions.forEach((q: any) => {
              if (q.premium) premiumQuestionIds.add(q.id);
            });
          });

          const matchingResponses = response.data.filter(
            (r: any) => r.assessmentId === assessment.id || r.assessmentSlug === assessment.slug
          );

          const latestResponse = matchingResponses.find((r: any) => {
            if (!r.answerDetails) return false;
            const answeredIds = Object.keys(r.answerDetails);
            return !answeredIds.some((qid) => premiumQuestionIds.has(qid));
          });

          if (latestResponse?.answerDetails) {
            const prefilled = buildPrefilledResponsesFromAnswerDetails(
              latestResponse.answerDetails,
              sortedCategories
            );

            if (Object.keys(prefilled).length > 0) {
              batchSetResponses(prefilled);
            }
          }
        }
      } catch {
        // ignore
      } finally {
        setFreeResponsesLoaded(true);
      }
    };

    fetchFreeResponses();
  }, [userEmail, assessment.id, assessment.slug, freeResponsesLoaded, sortedCategories, isFreshAttempt, batchSetResponses, assessment.categories]);

  // When user clicks "Let's get started" on a fresh attempt → prompt for a name first.
  const handleGetStarted = () => {
    if (pendingNamePrompt) {
      setRenameOpen(true);
    } else {
      handleStartAssessment();
    }
  };

  const handleSaveName = async (newName: string) => {
    const trimmedName = newName.trim();
    const nameTaken = await isAttemptNameTaken(trimmedName, attemptId);
    if (nameTaken) {
      toast({
        variant: 'destructive',
        title: 'Name already in use',
        description: 'Please choose a different assessment name.'
      });
      return false;
    }

    setCustomName(trimmedName);
    try {
      await updateAttemptName(attemptId, trimmedName);
    } catch {
      toast({ variant: 'destructive', title: 'Could not save name', description: 'Your changes may not have been saved.' });
      return false;
    }
    if (pendingNamePrompt) {
      setPendingNamePrompt(false);
      handleStartAssessment();
    }
    return true;
  };

  const handleFullRestart = () => {
    clearAllResponses();
    handleRestartAssessment();
  };

  const totalQuestions = sortedCategories.reduce((sum, c) => sum + c.questions.length, 0);
  const completedCount = Object.values(responses).filter((r) => isQuestionFullyAnswered(r)).length;
  const progressPercentage = calculateProgressPercentage(totalQuestions, completedCount);

  const currentCategoryQuestionsCount = currentCategory ? currentCategory.questions.length : 0;
  const currentCategoryCompletedCount = currentCategory
    ? currentCategory.questions.filter((q) => isQuestionFullyAnswered(responses[q.id])).length
    : 0;

  const handleMoveToNextQuestion = async () => {
    const isLastCategory = currentCategoryIndex >= sortedCategories.length - 1;
    const isLastQuestion = currentCategory && currentQuestionIndex >= currentCategory.questions.length - 1;

    if (isLastCategory && isLastQuestion) {
      const validation = validateAllRequiredQuestionsAnswered(responses, sortedCategories);
      if (!validation.valid) {
        toast({
          variant: 'destructive',
          title: 'Incomplete Assessment',
          description: `Please answer all required questions before submitting. (${validation.unansweredQuestions.length} questions remaining)`
        });
        return;
      }
      try {
        const { data: { user } } = await supabase.auth.getUser();
        const { data: orgMember } = await supabase
          .from('organization_members')
          .select('full_name, email')
          .eq('id', user?.id || '')
          .maybeSingle();

        const autoInfo = {
          fullName: orgMember?.full_name || user?.user_metadata?.full_name || '',
          email: user?.email || '',
          company: '',
          designation: '',
          consentGiven: true,
        };

        setPersonalInfo(autoInfo);
        const success = await submitAssessment(autoInfo);
        if (success) {
          // Clean up THIS attempt's progress only — leave other attempts alone.
          clearProgressFromLocalStorage(assessment.id, attemptId);
          localStorage.removeItem(`assessment_${assessment.id}_attempt_${attemptId}_responses`);
          deleteAttempt(attemptId).catch(() => {});
          completeAssessment();
        }
      } catch {
        toast({ variant: 'destructive', title: 'Submission failed', description: 'Please try again.' });
      }
    } else {
      moveToNextQuestion(responses);
    }
  };

  const handleNavigateToQuestion = (categoryIndex: number, questionIndex: number) => {
    navigateToQuestion(categoryIndex, questionIndex);
  };

  const defaultName = customName?.trim() || assessment.title;

  return (
    <div className="min-h-screen flex flex-col">
      <PremiumAssessmentHeader
        assessmentTitle={assessment.title}
        customName={customName}
        userEmail={userEmail}
        onGoHome={onBack}
        onEditName={() => setRenameOpen(true)}
      />
      <div className="flex-1 flex flex-col">
        <AssessmentLayout
          assessment={assessment}
          categories={sortedCategories}
          currentCategoryIndex={currentStep === 'questions' ? currentCategoryIndex : -1}
          currentStep={currentStep}
          currentQuestionIndex={currentQuestionIndex}
          responses={responses}
          onNavigateToQuestion={handleNavigateToQuestion}
        >
          {currentStep === 'questions' && (
            <AssessmentProgress
              percentage={progressPercentage}
              currentCategory={currentCategory?.name}
              currentQuestionIndex={currentQuestionIndex >= 0 ? currentQuestionIndex : undefined}
              totalQuestions={currentCategoryQuestionsCount}
              completedQuestions={currentCategoryCompletedCount}
            />
          )}

          <div className={currentStep !== 'welcome' ? 'mt-4' : ''}>
            <AssessmentContent
              currentStep={currentStep}
              assessment={assessment}
              categories={sortedCategories}
              currentCategoryIndex={currentCategoryIndex}
              currentQuestionIndex={currentQuestionIndex}
              responses={responses}
              scoresByCategory={calculatedScores}
              personalInfo={personalInfo}
              onStart={handleGetStarted}
              onRestart={handleFullRestart}
              onExit={onBack}
              onBack={moveToPreviousQuestion}
              onNext={handleMoveToNextQuestion}
              onResponseChange={handleResponseChange}
              onPersonalInfoSubmit={async () => {}}
              canMoveForward={currentQuestion ? canMoveForward(currentQuestion) : true}
              isSubmitting={isSubmitting}
              isPremium={true}
            />
          </div>
        </AssessmentLayout>
      </div>

      <RenameAssessmentDialog
        open={renameOpen}
        onOpenChange={(open) => {
          setRenameOpen(open);
          // If user dismisses the initial naming prompt without saving, start with the default name.
          if (!open && pendingNamePrompt) {
            setPendingNamePrompt(false);
            if (!customName) {
              // Persist the default name so the dashboard / header reflect it.
              const fallback = defaultName;
              setCustomName(fallback);
              updateAttemptName(attemptId, fallback).catch(() => {});
            }
            handleStartAssessment();
          }
        }}
        currentName={customName || ''}
        defaultName={defaultName}
        onSave={handleSaveName}
        title={pendingNamePrompt ? 'Name this assessment' : 'Rename assessment'}
        description={
          pendingNamePrompt
            ? 'Give this attempt a name so you can easily identify it later. You can change it any time.'
            : 'Give this assessment a name so you can easily identify it later.'
        }
      />
    </div>
  );
};

/** Get ALL categories with non-disabled questions (both free and premium).
 *  Within each category, free questions come first, then premium questions. */
function getAllCategoriesWithQuestions(assessment: Assessment): Category[] {
  const categories = assessment.categories
    .filter((c) => c.questions.some((q) => !q.disabled))
    .map((c) => ({
      ...c,
      questions: [...c.questions.filter((q) => !q.disabled)].sort((a, b) => {
        if (a.premium !== b.premium) return a.premium ? 1 : -1;
        return 0;
      }),
    }));

  return [...categories].sort((a, b) => {
    if (a.order !== undefined && b.order !== undefined) return a.order - b.order;
    return 0;
  });
}

export default PremiumAssessment;
